import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, RefreshCw, Crosshair, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "./api";

const pinIcon = (color) =>
  L.divIcon({
    className: "",
    html: `<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);
      background:${color};border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4)"></div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 26],
    popupAnchor: [0, -24],
  });

const ICONS = { quote: pinIcon("#0EA5A5"), order: pinIcon("#E5322A") };

function FitBounds({ points, focus }) {
  const map = useMap();
  useEffect(() => {
    if (focus) { map.flyTo([focus.lat, focus.lon], 16, { duration: 0.8 }); return; }
    if (!points.length) return;
    if (points.length === 1) { map.setView([points[0].lat, points[0].lon], 13); return; }
    map.fitBounds(points.map((p) => [p.lat, p.lon]), { padding: [40, 40] });
  }, [points, focus, map]);
  return null;
}

const dt = (v) => new Date(v).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function ShareMapModule() {
  const [rows, setRows] = useState(null);
  const [type, setType] = useState("all");
  const [onlyGps, setOnlyGps] = useState(false);
  const [focus, setFocus] = useState(null);

  const load = async () => {
    setRows(null);
    try {
      const { data } = await api.get("/share-views/map");
      setRows(data || []);
    } catch (e) { toast.error("Konum verileri yüklenemedi"); setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const points = useMemo(() => (rows || []).filter((r) =>
    (type === "all" || r.refType === type) && (!onlyGps || r.source === "gps")
  ), [rows, type, onlyGps]);

  const TABS = [
    { id: "all", label: "Tümü" },
    { id: "quote", label: "Teklifler" },
    { id: "order", label: "Siparişler" },
  ];

  return (
    <div className="space-y-4 zen-anim-up" data-testid="share-map-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold flex items-center gap-2.5">
          <MapPin className="w-5 h-5 text-[#E5322A]" /> Açılış Haritası
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg overflow-hidden border border-slate-300 dark:border-[#22304A]">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`share-map-tab-${t.id}`}
                onClick={() => { setType(t.id); setFocus(null); }}
                className={`px-3 py-1.5 text-[11px] font-semibold ${type === t.id ? "bg-[#E5322A] text-white" : "bg-transparent text-slate-500"}`}
              >{t.label}</button>
            ))}
          </div>
          <button
            data-testid="share-map-gps-toggle"
            onClick={() => { setOnlyGps((v) => !v); setFocus(null); }}
            className={`zen-btn-ghost !text-xs ${onlyGps ? "!text-emerald-600" : ""}`}
          ><Crosshair className="w-3.5 h-3.5" /> {onlyGps ? "Sadece kesin konum" : "Tümü (IP dahil)"}</button>
          <button onClick={load} className="zen-btn-ghost !text-xs" data-testid="share-map-refresh-btn"><RefreshCw className="w-3.5 h-3.5" /> Yenile</button>
        </div>
      </div>

      {rows === null ? (
        <div className="zen-panel p-10 flex items-center justify-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor...
        </div>
      ) : points.length === 0 ? (
        <div className="zen-panel p-10 text-center text-sm text-slate-500" data-testid="share-map-empty">
          Henüz konumlu bir açılış kaydı yok. WhatsApp'tan gönderilen teklif/sipariş linki açıldığında pin burada görünür.
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_320px] gap-4">
          <div className="zen-panel overflow-hidden" style={{ height: 560 }} data-testid="share-map-canvas">
            <MapContainer center={[39.0, 35.0]} zoom={6} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
              <TileLayer
                attribution='&copy; OpenStreetMap'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitBounds points={points} focus={focus} />
              {points.map((p) => (
                <Marker key={p.id} position={[p.lat, p.lon]} icon={ICONS[p.refType] || ICONS.quote}>
                  <Popup>
                    <div style={{ fontSize: 12, lineHeight: 1.5, minWidth: 190 }}>
                      <b>{p.refType === "order" ? "Sipariş" : "Teklif"} {p.docNo}</b><br />
                      {p.customerName}<br />
                      {dt(p.at)}<br />
                      {p.address || "-"}<br />
                      <span style={{ color: p.source === "gps" ? "#059669" : "#888" }}>
                        {p.source === "gps" ? `kesin konum${p.accuracy ? ` ±${Math.round(p.accuracy)} m` : ""}` : "yaklaşık (IP)"}
                      </span><br />
                      <a href={`https://www.google.com/maps?q=${p.lat},${p.lon}`} target="_blank" rel="noreferrer">Google Maps'te aç</a>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>

          <div className="zen-panel p-3 overflow-y-auto" style={{ maxHeight: 560 }}>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
              {points.length} konumlu açılış
            </div>
            <div className="space-y-1.5">
              {points.map((p) => (
                <button
                  key={p.id}
                  data-testid={`share-map-item-${p.id}`}
                  onClick={() => setFocus(p)}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-[#131B2E] transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-bold">
                      <span className={p.refType === "order" ? "text-[#E5322A]" : "text-teal-600"}>
                        {p.refType === "order" ? "Sipariş" : "Teklif"}
                      </span>{" "}{p.docNo}
                    </span>
                    <span className="text-[10px] text-slate-500 zen-mono">{dt(p.at)}</span>
                  </div>
                  <div className="text-[11px] font-semibold mt-0.5">{p.customerName}</div>
                  <div className="text-[10px] text-slate-500 leading-snug">
                    {p.address || "-"} · {p.source === "gps" ? `kesin ±${Math.round(p.accuracy || 0)} m` : "yaklaşık (IP)"}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
